// SPDX-License-Identifier: Elastic-2.0
/**
 * Fixture-mode entry point for the in-browser dev demo.
 *
 * Activation: set `VITE_FIXTURE_MODE=1` (the `npm run dev:fixture`
 * script does this). `enableFixtureMode()` then:
 *
 *  1. Seeds localStorage with a fake cloud-mode session so the
 *     AuthGuard releases on first nav.
 *  2. Boots an MSW service worker that intercepts every backend call
 *     the dashboard journey makes, serving canned fixture data.
 *
 * Called from `main.tsx` BEFORE `initAuth()` / `initGatewayClient()`
 * so the seed values are in storage by the time the auth store
 * hydrates and the gateway client makes its first request.
 *
 * No-op (returns immediately) when fixture mode isn't enabled, so the
 * import is safe even in production builds — Vite's tree-shaker can't
 * eliminate the import cleanly across the dynamic boot path, so a
 * runtime guard is the right tool.
 */
import { fixtureHandlers } from "./handlers";
import { seedFixtureSession } from "./seed";

export const FIXTURE_MODE_ENABLED: boolean =
  import.meta.env.VITE_FIXTURE_MODE === "1";

/**
 * Start MSW + seed the session. Returns a promise the boot path
 * awaits before rendering — otherwise the first /me request could
 * race the worker registration and slip through to a real network.
 */
export async function enableFixtureMode(): Promise<void> {
  if (!FIXTURE_MODE_ENABLED) return;

  // Seed FIRST so the auth store sees the cloud session on its
  // initial hydrate (which happens during initAuth, called after this
  // function returns).
  seedFixtureSession();

  // Dynamic import keeps the entire MSW client bundle out of
  // production builds (Vite tree-shakes the unused branch since the
  // import lives inside an `if (FIXTURE_MODE_ENABLED)` guard).
  const { setupWorker } = await import("msw/browser");
  const worker = setupWorker(...fixtureHandlers);

  // Quiet the default banner so the dev console isn't polluted —
  // the `[fixture-mode]` log line below is the explicit signal.
  await worker.start({
    onUnhandledRequest: "bypass",
    quiet: true,
  });

  // Single console.log so a developer skimming devtools knows
  // they're not talking to a real backend.
  // eslint-disable-next-line no-console
  console.log(
    "%c[fixture-mode]%c MSW worker running — backend calls return canned fixtures. " +
      "Set VITE_FIXTURE_MODE='' to disable.",
    "color:#FFA069;font-weight:600",
    "color:inherit",
  );
}
